import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Col, Row, FormLabel, FormControl } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../../services/AxiosInstance";
import Api from "../../../../constants/Api";
import { ApiError } from "../../../components/Common/ApiError";
import { PanelLoading } from "../../../components/Common/LoadingPlaceholder";
import MonthSelect from "../../../components/Common/MonthSelect";
import YearSelect from "../../../components/Common/YearSelect";
import BranchSelect from "../../../components/Custom/Main/Branch/BranchSelect";
import PerformancePlanSelect from "../../../components/Custom/Performance/PerformancePlanSelect";
import CustomSelect from "../../../components/Forms/Element/CustomSelect";
import { warnAlert } from "../../../components/alert/defaultAlert";
import {
    endYear,
    startYear,
    formatMonthNumericValueToString,
} from "../../../../utils/DateUtils";
import { PerformanceWorkspace, PerformancePanel } from "./common/PerformanceWorkspace";
import {
    describeNeutralIndicator,
    describeReturnRate,
    formatCurrency,
    formatPercent,
    formatIndicatorValue
} from "./common/performanceFormat";
import EmployeeAvatar from "./common/EmployeeAvatar";
import SubscriptionsWithoutSellerNotice from "./common/SubscriptionsWithoutSellerNotice";
import CategoryTicketPanel from "./CategoryTicketPanel";
import MenuConfig from "../../../../constants/MenuConfig";
import { useBranch } from "../../../../context/BranchContext";
import "./PerformanceDashboard.css";

const PerformanceDashboard = () => {
    const { t } = useTranslation();
    const { selectedBranch } = useBranch();
    const navigate = useNavigate();

    const [showLoading, setShowLoading] = useState(false);
    const [items, setItems] = useState([]);
    // O dashboard abre no mês atual, como as demais telas por período (decisão de 07/10/2026).
    // O selo de destaque do agendamento público continua no último mês encerrado; para ver o
    // mesmo vencedor do selo, o gestor escolhe o mês anterior no filtro.
    const currentMonth = new Date();
    const [month, setMonth] = useState({ value: currentMonth.getMonth() + 1, label: currentMonth.getMonth() + 1 });
    const [year, setYear] = useState({ value: currentMonth.getFullYear(), label: currentMonth.getFullYear() });
    const [branchFilter, setBranchFilter] = useState(null);
    const [planFilter, setPlanFilter] = useState(null);
    const [levelFilter, setLevelFilter] = useState(null);
    const [searchName, setSearchName] = useState("");
    // Mantém o texto em edição separado do filtro aplicado para evitar que cada tecla
    // altere a lista antes de o usuário confirmar a pesquisa.
    const [appliedSearchName, setAppliedSearchName] = useState("");
    const [highlightRanking, setHighlightRanking] = useState([]);
    const [highlightScope, setHighlightScope] = useState(null);
    // O ranking é uma consulta à parte do resumo; o pódio mostra o próprio carregamento enquanto ela não volta.
    const [highlightLoading, setHighlightLoading] = useState(false);
    // Recorte da última pesquisa para o quadro de ticket por categoria. Separado do highlightScope,
    // que só é gravado quando o ranking responde: o quadro não depende do destaque para carregar.
    const [categoryTicketScope, setCategoryTicketScope] = useState(null);
    // Totais do topo calculados no backend: faturamento e atendimentos incluem quem não tem
    // plano; clientes são contados uma vez, mesmo entre profissionais diferentes.
    const [totals, setTotals] = useState(null);

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        const nextBranch = selectedBranch
            ? { value: selectedBranch.id, label: selectedBranch.name }
            : null;
        setBranchFilter(nextBranch);
        onSearch(nextBranch);
    }, [selectedBranch?.id]);
    /* eslint-enable react-hooks/exhaustive-deps */

    const onSearch = (selectedBranchFilter = branchFilter, nameFilter = searchName) => {
        if (!month) {
            warnAlert(t("validation.required.month"));
            return;
        }
        if (!year) {
            warnAlert(t("validation.required.year"));
            return;
        }

        setAppliedSearchName(nameFilter);

        const startDate = `${year.value}-${formatMonthNumericValueToString(month.value)}-01`;
        const lastDay = new Date(year.value, month.value, 0).getDate();
        const endDate = `${year.value}-${formatMonthNumericValueToString(month.value)}-${lastDay}`;

        setShowLoading(true);
        setTotals(null);
        setHighlightRanking([]);
        setHighlightScope(null);
        setHighlightLoading(true);
        setCategoryTicketScope({ startDate, endDate, branchId: selectedBranchFilter?.value || null });
        axiosInstance
            .get(Api.performance.dashboard + "/overview", {
                params: {
                    startDate,
                    endDate,
                    branchId: selectedBranchFilter?.value || undefined,
                    planId: planFilter?.value || undefined,
                    levelOrder: levelFilter?.value !== "WITHOUT_LEVEL" ? levelFilter?.value : undefined,
                    withoutLevel: levelFilter?.value === "WITHOUT_LEVEL" || undefined
                }
            })
            .then((resp) => {
                setItems(resp.data?.employees || []);
                setTotals({
                    revenue: resp.data?.totalRevenue ?? 0,
                    appointments: resp.data?.totalAppointments ?? 0,
                    customers: resp.data?.totalCustomers ?? 0
                });
            })
            .catch((err) => ApiError(err))
            .finally(() => setShowLoading(false));

        // Ranking do "colaborador destaque do mês" (quantidades × pesos por critério),
        // independente de plano/nível de performance - por isso é buscado à parte do resumo acima.
        axiosInstance
            .get(Api.performance.highlight, {
                params: {
                    startDate,
                    endDate,
                    branchId: selectedBranchFilter?.value || undefined
                }
            })
            .then((resp) => {
                setHighlightRanking(resp.data || []);
                // As datas vão junto para o aviso de assinaturas sem vendedor usar o mesmo recorte.
                setHighlightScope({ month: month.value, year: year.value, branch: selectedBranchFilter, startDate, endDate });
            })
            .catch((err) => {
                setHighlightRanking([]);
                setHighlightScope(null);
                ApiError(err);
            })
            .finally(() => setHighlightLoading(false));
    };

    // A análise individual é uma página própria (gráficos, projeção e simulação por nível) e
    // recebe o mês e a filial do painel pela URL, para abrir no mesmo recorte.
    const openEmployeeAnalysis = (employee) => {
        const params = new URLSearchParams({ month: month.value, year: year.value });
        if (branchFilter?.value) {
            params.set("branchId", branchFilter.value);
        }
        navigate(`${MenuConfig.performance.dashboard.route}/employee/${employee.employeeId}?${params.toString()}`);
    };

    // O relatório abre no mesmo mês e filial vistos no pódio, sem exigir refazer os filtros.
    const openHighlightReport = () => {
        const params = new URLSearchParams({ month: highlightScope.month, year: highlightScope.year });
        if (highlightScope.branch?.value) {
            params.set("branchId", highlightScope.branch.value);
            params.set("branchName", highlightScope.branch.label);
        }
        navigate(`${MenuConfig.performance.monthly_highlight.route}?${params.toString()}`);
    };


    const renderProgressBar = (value, target, label, format, key) => {
        if (!target || target <= 0) return null;
        const percent = Math.min((value / target) * 100, 100);
        const achieved = value >= target;
        return (
            <div className="mb-3" key={key}>
                <div className="d-flex justify-content-between mb-1 align-items-center">
                    <small className="fw-semibold text-muted" style={{ fontSize: '0.75rem' }}>{label}</small>
                    <small className={achieved ? "text-success fw-bold" : "employee-performance-card__progress-value fw-medium"} style={{ fontSize: '0.75rem' }}>
                        {format(value)} / {format(target)}
                    </small>
                </div>
                <div className="progress employee-performance-card__progress">
                    <div
                        className={`progress-bar ${achieved ? "bg-success" : "bg-primary"}`}
                        role="progressbar"
                        style={{ width: `${percent}%`, borderRadius: '4px', transition: 'width 1s ease-in-out' }}
                    />
                </div>
            </div>
        );
    };

    const renderEmployeeCard = (emp) => {
        const levels = Array.from(
            new Map((emp.levelProgress || emp.levels || []).map((level) => [level.levelId || level.levelOrder, level])).values()
        );
        // O próximo nível vem do backend (PerformanceLevelEvaluator): no plano de carreira o primeiro
        // nível é a entrada e nunca é o "próximo", e repetir a regra aqui faria as telas divergirem.
        const nextLevel = emp.nextLevelOrder != null ? levels.find((l) => l.levelOrder === emp.nextLevelOrder) : null;
        const metricLevel = nextLevel || [...levels].reverse().find((l) => l.achieved) || levels[0];
        const planIndicators = metricLevel?.indicators || [];

        return (
            <Col xl={4} lg={6} key={emp.employeeId}>
                <PerformancePanel 
                    className="employee-performance-card h-100"
                    style={emp.currentLevelColor ? { 
                        borderColor: `${emp.currentLevelColor}66`,
                        boxShadow: `0 15px 35px ${emp.currentLevelColor}15`
                    } : {}}
                >
                    <div
                        className="employee-performance-card__media"
                        style={emp.currentLevelColor ? { borderColor: `${emp.currentLevelColor}55` } : {}}
                    >
                        <EmployeeAvatar
                            employeeId={emp.employeeId}
                            name={emp.employeeName}
                        />
                        <div className="employee-performance-card__media-shade" />
                        <div className="employee-performance-card__identity">
                            <div 
                                className={`employee-performance-card__level-badge ${emp.currentLevelName ? "" : "employee-performance-card__level-badge--empty"}`}
                                style={emp.currentLevelColor ? { backgroundColor: emp.currentLevelColor, color: '#fff', boxShadow: `0 4px 12px ${emp.currentLevelColor}44` } : {}}
                            >
                                {emp.currentLevelName || t("performance.no_level")}
                            </div>
                            <h5>{emp.employeeName}</h5>
                            <p>{emp.planName || t("performance.no_plan")}</p>
                        </div>
                    </div>

                    <div className="employee-performance-card__body">
                        {planIndicators.length > 0 && (
                            <div className="employee-performance-card__metric-grid">
                                {planIndicators.map((indicator, index) => (
                                    <div
                                        className="employee-performance-card__metric-box"
                                        key={`${indicator.name}-${index}`}
                                        // A taxa de retorno traz a conta no título: quantos avulsos entraram e quantos voltaram.
                                        title={indicator.type === "RETURN_RATE" ? describeReturnRate(emp.returnRateDetail) : undefined}
                                    >
                                        <span className="employee-performance-card__metric-value">
                                            {indicator.insufficientBase ? "—" : formatIndicatorValue(indicator, indicator.actualValue)}
                                        </span>
                                        <span className="employee-performance-card__metric-label">{indicator.name}</span>
                                        <span className="employee-performance-card__metric-target">
                                            {indicator.insufficientBase
                                                ? describeNeutralIndicator(indicator)
                                                : `Meta ${formatIndicatorValue(indicator, indicator.targetValue)}`}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}

                        {nextLevel && (
                            <div className="employee-performance-card__progress-section">
                                <div className="employee-performance-card__progress-label">
                                    <i className="fas fa-arrow-trend-up text-primary"></i>
                                    {t("performance.next_level")}: <span className="text-primary">{nextLevel.levelName}</span>
                                </div>
                                {nextLevel.indicators?.map((ind, i) => {
                                    // Sem base para calcular, uma barra em 0% diria que falta tudo; a meta
                                    // nem conta para o nível, então a tela diz isso.
                                    if (ind.insufficientBase) {
                                        return (
                                            <p className="small text-muted mb-3" key={i}>
                                                {ind.name}: {describeNeutralIndicator(ind).toLowerCase()}, não conta para o nível.
                                            </p>
                                        );
                                    }
                                    return renderProgressBar(
                                        Number(ind.actualValue || 0),
                                        Number(ind.targetValue),
                                        ind.name,
                                        (value) => formatIndicatorValue(ind, value),
                                        i
                                    );
                                })}
                            </div>
                        )}

                        <div className="employee-performance-card__all-levels">
                            {levels.map((lp) => (
                                <div
                                    key={lp.levelOrder}
                                    className={`employee-performance-card__level-dot ${lp.achieved ? "employee-performance-card__level-dot--achieved" : ""}`}
                                    style={lp.achieved && lp.color ? { backgroundColor: lp.color, borderColor: lp.color } : {}}
                                    title={lp.levelName}
                                >
                                    {lp.levelOrder}
                                </div>
                            ))}
                        </div>
                        <Button
                            variant="outline-primary"
                            size="sm"
                            className="employee-performance-card__detail-button"
                            onClick={() => openEmployeeAnalysis(emp)}
                        >
                            <i className="fas fa-chart-line me-2"></i>
                            Ver análise
                        </Button>
                    </div>
                </PerformancePanel>
            </Col>
        );
    };

    const levelOptions = useMemo(() => {
        const levels = new Map();
        items.forEach((item) => {
            if (item.currentLevelOrder == null) {
                return;
            }
            levels.set(item.currentLevelOrder, {
                value: item.currentLevelOrder,
                label: item.currentLevelName || `${t("performance.level")} ${item.currentLevelOrder}`
            });
        });
        return [
            { value: "WITHOUT_LEVEL", label: t("performance.no_level") },
            ...Array.from(levels.values()).sort((a, b) => a.value - b.value)
        ];
    }, [items, t]);

    // A equipe inteira entra no total de atendimentos, inclusive quem não tem plano.
    const totalAppointments = totals?.appointments;
    const averageTicket = totalAppointments > 0 ? totals.revenue / totalAppointments : 0;
    // Taxa de retorno do grupo, ponderada pela base de cada colaborador (avulsos cujo prazo venceu
    // no período, ver ReturnRateService). A média simples das taxas dava o mesmo peso a quem tem
    // 10 e a quem tem 150 clientes na base. Quem ficou abaixo da base mínima entra na soma: no
    // grupo a base é grande, e deixar essas pessoas de fora esconderia clientes atendidos.
    const sumOfReturnBase = items.reduce((acc, item) => acc + (item.returnRateDetail?.customers || 0), 0);
    const sumOfReturned = items.reduce((acc, item) => acc + (item.returnRateDetail?.returned || 0), 0);
    const avgReturnRate = sumOfReturnBase > 0 ? (sumOfReturned * 100) / sumOfReturnBase : 0;
    const totalCustomers = totals?.customers ?? "-";

    // Filtrar colaboradores por nome
    const filteredItems = items.filter(item => {
        if (!appliedSearchName.trim()) return true;
        return item.employeeName?.toLowerCase().includes(appliedSearchName.toLowerCase());
    });

    // Os quatro cartões saem da mesma consulta do resumo (/overview): carregam juntos.
    const summaryCards = [
        {
            label: t("performance.total_appointments"),
            value: totalAppointments ?? "-",
            detail: t("performance.total_appointments_detail"),
            icon: "fa-calendar-check",
            tone: "sky",
            loading: showLoading
        },
        {
            label: t("performance.total_customers"),
            value: totalCustomers,
            detail: t("performance.total_customers_detail"),
            icon: "fa-users",
            tone: "coral",
            loading: showLoading
        },
        {
            label: t("performance.avg_return_rate"),
            value: formatPercent(avgReturnRate),
            detail: t("performance.avg_return_rate_detail"),
            icon: "fa-rotate-left",
            tone: "amber",
            loading: showLoading
        },
        {
            label: t("performance.overall_avg_ticket"),
            value: totals ? formatCurrency(averageTicket) : "-",
            detail: t("performance.overall_avg_ticket_detail"),
            icon: "fa-receipt",
            tone: "green",
            loading: showLoading
        }
    ];

    return (
        <PerformanceWorkspace
            eyebrow={t("performance.label")}
            title={t("performance.dashboard")}
            subtitle={t("performance.dashboard_subtitle")}
            summaryCards={summaryCards}
        >
            <PerformancePanel
                title={t("period_filter")}
                className="mb-4"
            >
                {/* Grade que fecha as 12 colunas em cada faixa: no computador, duas linhas cheias
                    (Mês, Ano, Filial e Plano; depois Nível, Nome e Buscar); no tablet, dois campos
                    por linha; no celular, um por linha. Com larguras "auto" sobrava espaço vazio. */}
                <Row className="align-items-end g-3">
                    <Col xs={12} sm={6} lg={2}>
                        <FormLabel className="small fw-bold">{t("month.label")}</FormLabel>
                        <MonthSelect
                            value={month}
                            onChange={setMonth}
                        />
                    </Col>
                    <Col xs={12} sm={6} lg={2}>
                        <FormLabel className="small fw-bold">{t("year")}</FormLabel>
                        <YearSelect
                            value={year}
                            onChange={setYear}
                            startYear={startYear()}
                            endYear={endYear()}
                        />
                    </Col>
                    <Col xs={12} sm={6} lg={4}>
                        <FormLabel className="small fw-bold">{t("branch")}</FormLabel>
                        <div className="performance-dashboard__branch-select">
                            <BranchSelect
                                value={branchFilter}
                                onChange={setBranchFilter}
                                // A filial definida no cabeçalho é o recorte obrigatório da tela.
                                // Com o contexto em "Todas", o usuário pode escolher uma filial só para esta consulta.
                                isDisabled={Boolean(selectedBranch)}
                            />
                        </div>
                    </Col>
                    <Col xs={12} sm={6} lg={4}>
                        <FormLabel className="small fw-bold">{t("performance.plan")}</FormLabel>
                        <PerformancePlanSelect
                            value={planFilter}
                            onChange={setPlanFilter}
                        />
                    </Col>
                    <Col xs={12} sm={6} lg={4}>
                        <FormLabel className="small fw-bold">{t("performance.current_level")}</FormLabel>
                        <CustomSelect
                            value={levelFilter}
                            onChange={setLevelFilter}
                            options={levelOptions}
                            isClearable
                            placeholder={t("select")}
                        />
                    </Col>
                    <Col xs={12} sm={6} lg={5}>
                        <FormLabel className="small fw-bold">{t("name")}</FormLabel>
                        <FormControl
                            type="text"
                            placeholder={t("performance.search_employee_placeholder")}
                            value={searchName}
                            onChange={(e) => setSearchName(e.target.value)}
                        />
                    </Col>
                    <Col xs={12} lg={3}>
                        <Button variant="primary" onClick={() => onSearch(branchFilter, searchName)} className="w-100">
                            <i className="fas fa-search me-2"></i>
                            {t("search")}
                        </Button>
                    </Col>
                </Row>
            </PerformancePanel>

            {/* O botão do relatório só aparece com o ranking carregado: ele abre com o recorte da
                resposta (highlightScope), que não existe durante o carregamento. */}
            {(highlightLoading || highlightRanking.length > 0) && (
                <PerformancePanel
                    title={t("performance.highlight_of_month")}
                    className="mb-4"
                    action={!highlightLoading && <Button variant="outline-primary" size="sm" onClick={openHighlightReport}>
                        {t("performance.reports.view_full_ranking")}
                    </Button>}
                >
                    {highlightLoading ? (
                        <PanelLoading height={160} label="Calculando o destaque do mês..." />
                    ) : (
                        <>
                            <p className="text-muted small mb-2">Clientes atendidos, serviços, produtos e assinaturas vendidas dão pontos. Cada um vale uma quantidade diferente de pontos.</p>
                            {/* A integração cria contratos sem vendedor, que não pontuam para ninguém. O aviso
                                diz quantos são e leva à lista para informar quem vendeu. */}
                            <SubscriptionsWithoutSellerNotice
                                startDate={highlightScope?.startDate}
                                endDate={highlightScope?.endDate}
                                branchId={highlightScope?.branch?.value}
                            />
                            <Row className="g-3">
                                {highlightRanking.slice(0, 3).map((emp) => (
                                    <Col xs={12} md={4} key={emp.employeeId}>
                                        <div className={`employee-highlight-podium-card employee-highlight-podium-card--rank-${emp.rank} d-flex align-items-center gap-3 p-3`}>
                                            <div className="employee-highlight-podium-card__rank" title={`${emp.rank}º lugar`}>
                                                <i className={`fas ${emp.rank === 1 ? "fa-trophy" : "fa-medal"}`} aria-hidden="true"></i>
                                                <span>{emp.rank}º</span>
                                            </div>
                                            <div className="employee-highlight-podium-card__avatar">
                                                <EmployeeAvatar
                                                    employeeId={emp.employeeId}
                                                    name={emp.employeeName}
                                                />
                                            </div>
                                            <div>
                                                <h6 className="mb-0">{emp.employeeName}</h6>
                                                <strong>{emp.combinedScore?.toLocaleString("pt-BR")} pontos</strong>
                                            </div>
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        </>
                    )}
                </PerformancePanel>
            )}

            {/* Fora dos filtros de plano e nível de propósito: a comparação é da equipe que atendeu. */}
            <CategoryTicketPanel scope={categoryTicketScope} />

            {showLoading ? (
                <PanelLoading height={360} label="Calculando o desempenho da equipe..." />
            ) : items.length === 0 ? (
                <div className="performance-dashboard__empty-state text-center py-5 rounded-4">
                    <i className="fas fa-folder-open fa-3x mb-3 opacity-25"></i>
                    <p className="fs-5">{t("performance.no_plan_assigned")}</p>
                </div>
            ) : filteredItems.length === 0 ? (
                <div className="performance-dashboard__empty-state text-center py-5 rounded-4">
                    <i className="fas fa-magnifying-glass fa-3x mb-3 opacity-25"></i>
                    <p className="fs-5">{t("performance.no_employee_found")}</p>
                </div>
            ) : (
                // O navegador não deve traduzir nomes cadastrados nem abreviações dos indicadores
                // (por exemplo, PERFORMANCE virava DESEMPENHO e "Min" virava "Hortelã").
                <Row className="g-4 notranslate" translate="no">
                    {filteredItems.map((emp) => renderEmployeeCard(emp))}
                </Row>
            )}
        </PerformanceWorkspace>
    );
};

export default PerformanceDashboard;
